/**
 * @fileoverview Type-yes confirmation modal — danger variant of the standard
 *               application ConfirmActionModal, with a "Type yes" guard.
 * @module inspection-renderer/violations
 *
 * Responsibilities:
 * - Surface a destructive-action confirmation modal that matches the
 *   tenant-web / system-web / mobile-pwa standard ConfirmActionModal visual
 *   style (red danger header, AlertTriangle icon, black/50 backdrop, top
 *   z-index, escape-to-close, max-w-md white card).
 * - Lock the confirm button until the user has TYPED "yes" into the input.
 *   Comparison is trimmed + case-insensitive — match the everyday-language
 *   intent rather than enforcing keystroke fidelity.
 *
 * Dependencies:
 * - React 18 (peer).
 * - lucide-react (AlertTriangle, X) — already in inspection-renderer's deps.
 *
 * Architecture:
 * - Self-contained inside the inspection-renderer package. Each consumer app
 *   already ships its own ConfirmActionModal (tenant-web / system-web /
 *   mobile-pwa each have one in components/ui), but cross-package imports
 *   from the renderer into a host app would invert the dependency. Mirroring
 *   the same visual contract here keeps the renderer self-contained while
 *   matching the user's "use the standard confirmation box" requirement.
 *
 * Notes:
 * - No portal — rendered inline; the fixed-inset positioning + high z-index
 *   handles overlay placement.
 * - Confirm fires synchronously; consumers that need async work can wrap
 *   onConfirm themselves.
 *
 * @component TypeYesConfirmModal
 *
 * @contract TypeYesConfirmModalProps
 * Purpose: prop contract for the destructive-action "type yes" confirmation.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';

export interface TypeYesConfirmModalProps {
  /** Modal title, e.g. "Remove this violation occurrence?" */
  title: string;
  /** Body text explaining what is about to happen. */
  description: ReactNode;
  /** Label on the confirm button. Default "Yes, remove". */
  confirmButtonLabel?: string;
  /** Called when the inspector types "yes" and clicks confirm. */
  onConfirm: () => void;
  /** Called when the inspector cancels (Cancel button, Escape, backdrop click). */
  onClose: () => void;
}

/** Returns true when the input matches "yes" (trimmed, case-insensitive). */
function isYes(value: string): boolean {
  return value.trim().toLowerCase() === 'yes';
}

export function TypeYesConfirmModal({
  title,
  description,
  confirmButtonLabel = 'Yes, remove',
  onConfirm,
  onClose,
}: TypeYesConfirmModalProps): ReactNode {
  const [typed, setTyped] = useState('');
  const inputId = useId();

  // Escape closes — matches the standard ConfirmActionModal contract.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const canConfirm = isYes(typed);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canConfirm) return;
    onConfirm();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-modal="type-yes-confirm"
      className="fixed inset-0 z-100 flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        data-action="confirm-backdrop"
      />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col overflow-hidden">
        {/* Header (matches the standard ConfirmActionModal danger variant). */}
        <div className="px-4 py-3 border-b flex items-center gap-2 bg-red-50">
          <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
          <h3 className="font-semibold text-red-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            data-action="confirm-close"
            aria-label="Close"
            className="ml-auto transition-colors text-red-400 hover:text-red-600"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="text-sm text-gray-700">{description}</div>

          <div>
            <label htmlFor={inputId} className="block text-xs font-medium text-gray-700 mb-1">
              Type <span className="font-mono font-semibold text-red-700">yes</span> to confirm
            </label>
            <input
              id={inputId}
              type="text"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              data-input="confirm-yes"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-action="confirm-cancel"
              className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canConfirm}
              data-action="confirm-submit"
              className="px-3 py-2 text-sm rounded-md text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {confirmButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
